import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ForecastChart } from '@/components/explore/ForecastChart';
import { Radar, RefreshCw, Sliders, TrendingUp, AlertTriangle, ShieldCheck, Loader2, Bell, LayoutDashboard, HelpCircle, Info, LineChart, Microscope } from 'lucide-react';
import { getTerm, useTerms } from '@/lib/branding';

const CONF_CLS = { High: 'bg-emerald-500/15 text-emerald-300', Medium: 'bg-amber-500/15 text-amber-300', Low: 'bg-rose-500/15 text-rose-300', Unknown: 'bg-muted text-muted-foreground' };
const CONF_HELP = {
  High: 'High confidence — the model backtests with low error (MAPE < 10%). Projections are dependable.',
  Medium: 'Medium confidence — moderate backtest error (MAPE ~10–20%). Treat projections as directional, not exact.',
  Low: 'Low confidence — high backtest error or short history. Projections are rough; only warned on cautiously.',
  Unknown: 'Confidence not yet computed — run a scan to evaluate this metric.',
};
const STATUS_META = {
  projected_issue: { label: 'Projected issue', cls: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30', icon: AlertTriangle },
  ok: { label: 'Watching', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20', icon: ShieldCheck },
  low_confidence: { label: 'Low confidence', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/20', icon: TrendingUp },
  pending: { label: 'Pending scan', cls: 'bg-muted text-muted-foreground border-border/40', icon: Radar },
};

export default function ForecastSentinelsPage() {
  useTerms();
  const fsTerm = getTerm('forecast_sentinels');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = async (silent) => {
    try { const r = await api.getForecastSentinels(); setData(r); setForm(r.settings); return r; }
    catch (e) { toast.error(e.message || 'Failed to load sentinels'); return null; }
    finally { if (!silent) setLoading(false); }
  };

  // Kick off a background scan and poll for incremental results (each metric is
  // written as it finishes, so the list refreshes progressively).
  const runBackgroundScan = async () => {
    setRefreshing(true);
    try {
      await api.scanForecastSentinels({ background: true });
      for (let i = 0; i < 12; i++) {
        await new Promise(r => setTimeout(r, 6000));
        const r = await load(true);
        if (!r?.scanning) break;
      }
    } catch { /* keep cached */ }
    finally { setRefreshing(false); }
  };

  // On open: show cached status instantly, then auto-rescan in the background if
  // the last scan is stale or any metric is pending — so users never act on stale data.
  useEffect(() => {
    (async () => {
      const r = await load();
      if (!r?.settings?.enabled) return;
      const times = [...(r.sentinels || []), ...(r.not_watchable || [])].map(s => s.last_scan).filter(Boolean).sort();
      const newest = times[times.length - 1];
      const stale = !newest || (Date.now() - new Date(newest).getTime() > 30 * 60 * 1000);
      const pending = (r.sentinels || []).some(s => s.status === 'pending');
      if ((stale || pending) && !r.scanning) runBackgroundScan();
      else if (r.scanning) runBackgroundScan();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (kind) => {
    if (kind === 'scan') {
      setBusy('scan');
      try { await runBackgroundScan(); toast.success('Scan complete'); }
      finally { setBusy(''); }
      return;
    }
    setBusy(kind);
    try {
      const r = await api.tuneForecastSentinels();
      toast.success(`Tuned ${r.tuned || 0} metrics · switched ${r.switched || 0}`);
      await load(true);
    } catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setBusy(''); }
  };

  const toggleOptOut = async (s) => {
    try { await api.optOutForecastSentinel(s.sentinel_id, !s.opted_out); await load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };

  const saveSettings = async () => {
    try { const r = await api.updateForecastSentinelSettings(form); setData(d => ({ ...d, settings: r })); setSettingsOpen(false); toast.success('Settings saved'); }
    catch (e) { toast.error(e.message || 'Failed to save'); }
  };

  const demoTrigger = async () => {
    setBusy('demo');
    try { const r = await api.demoTriggerForecastSentinel(); if (r.ok) { toast.success(`Demo issue created on "${r.metric}" — open Insights to create an alert`); await load(); } else toast.error(r.error || 'No metric to demo on'); }
    catch (e) { toast.error(e.message || 'Demo failed'); }
    finally { setBusy(''); }
  };

  const mapSeries = async (s, field) => {
    const cur = s.series_field || '';
    if ((field || '') === cur) return;
    try {
      await api.setForecastSentinelSeriesField(s.sentinel_id, field || '');
      toast.success(field ? 'Mapped — re-forecasting…' : 'Reset to the metric\u2019s own definition — re-forecasting…');
      await load(true);
    } catch (e) { toast.error(e.message || 'Failed to update mapping'); }
  };

  const mapDateField = async (s, field) => {
    const cur = s.date_field || '';
    if ((field || '') === cur) return;
    try {
      await api.setForecastSentinelDateField(s.sentinel_id, field || '');
      toast.success(field ? 'Date field set — re-forecasting…' : 'Reset to auto-detected date — re-forecasting…');
      await load(true);
    } catch (e) { toast.error(e.message || 'Failed to update date field'); }
  };

  const [chartDlg, setChartDlg] = useState({ open: false });
  const openChart = async (s) => {
    setChartDlg({ open: true, sentinel: s, loading: true, forecast: null });
    try {
      const r = await api.previewForecastSentinel(s.sentinel_id);
      setChartDlg({ open: true, sentinel: s, loading: false, forecast: r?.forecast || null });
    } catch (e) {
      setChartDlg({ open: true, sentinel: s, loading: false, forecast: null });
      toast.error(e.message || 'Could not build the forecast chart');
    }
  };

  const seriesLabelFor = (s) => {
    const cands = data?.candidates?.[s.model_id] || [];
    const key = s.series_field || s.series_used;
    const hit = cands.find(c => c.name === key);
    return hit ? hit.label : (key ? String(key).replace(/_/g, ' ') : null);
  };

  const MapSelect = ({ s, placeholder }) => {
    const cands = data?.candidates?.[s.model_id] || [];
    if (cands.length === 0) return null;
    return (
      <select value={s.series_field || ''} onChange={e => mapSeries(s, e.target.value)}
        className="text-[10px] bg-background/60 border border-border/40 rounded px-1.5 py-1 text-muted-foreground max-w-[190px]"
        data-testid={`fs-map-${s.sentinel_id}`} title="Choose which underlying series this metric forecasts">
        <option value="">{placeholder}</option>
        {cands.map(c => <option key={c.name} value={c.name}>{c.label}</option>)}
      </select>
    );
  };

  const DateSelect = ({ s }) => {
    const cands = data?.date_candidates?.[s.model_id] || [];
    if (cands.length === 0) return null;
    return (
      <select value={s.date_field || ''} onChange={e => mapDateField(s, e.target.value)}
        className="text-[10px] bg-background/60 border border-border/40 rounded px-1.5 py-1 text-muted-foreground max-w-[190px]"
        data-testid={`fs-date-${s.sentinel_id}`} title="Choose which date field the forecast plots values over">
        <option value="">Date: Auto</option>
        {cands.map(c => <option key={c.name} value={c.name}>{c.label}</option>)}
      </select>
    );
  };

  const dateLabelFor = (s) => {
    const cands = data?.date_candidates?.[s.model_id] || [];
    const key = s.date_field || s.date_field_used;
    const hit = cands.find(c => c.name === key);
    return hit ? hit.label : (s.date_field_label || (key ? String(key).replace(/_/g, ' ') : null));
  };

  const [pinPicker, setPinPicker] = useState({ open: false });

  const openPin = async (s) => {
    try {
      const all = await api.getReports();
      const list = (all || [])
        .filter(rep => (rep.query_definition?.model_id || rep.model_id) === s.model_id)
        .sort((a, b) => new Date(b.updated_at || b.modified_at || b.created_at || 0) - new Date(a.updated_at || a.modified_at || a.created_at || 0));
      setPinPicker({ open: true, sentinel: s, dashboards: list, saving: false });
    } catch (e) { toast.error(e.message || 'Failed to load dashboards'); }
  };

  const openPinSummary = async () => {
    try {
      const all = await api.getReports();
      const list = (all || []).sort((a, b) => new Date(b.updated_at || b.modified_at || b.created_at || 0) - new Date(a.updated_at || a.modified_at || a.created_at || 0));
      setPinPicker({ open: true, summary: true, dashboards: list, saving: false });
    } catch (e) { toast.error(e.message || 'Failed to load dashboards'); }
  };

  const confirmPin = async (reportId) => {
    const s = pinPicker.sentinel;
    const isSummary = !!pinPicker.summary;
    setPinPicker(p => ({ ...p, saving: reportId }));
    try {
      let newVisual;
      const visualId = 'v_' + Date.now();
      if (isSummary) {
        newVisual = {
          id: visualId, vizType: 'sentinel_summary',
          title: 'Projected Issues This Week',
          rowDims: [], colDim: null, valueMeasures: [], filterDims: [], _reportPageIndex: 0,
        };
      } else {
        const horizon = data?.settings?.horizon || 6;
        const grain = data?.settings?.grain;
        const source_question = s.source_question || `forecast ${s.measure_label || s.label} for the next ${horizon} periods`;
        const res = await api.forecastRebuild({ model_id: s.model_id, source_question,
          unit: grain && grain !== 'auto' ? grain : undefined, horizon });
        const fc = res?.result?.forecast;
        if (!fc) { toast.error('Could not build this forecast'); setPinPicker(p => ({ ...p, saving: false })); return; }
        newVisual = {
          id: visualId, vizType: 'forecast',
          title: `${fc.measure_label || s.label} · Sentinel Forecast`.slice(0, 80),
          rowDims: [], colDim: null, valueMeasures: [], filterDims: [], _reportPageIndex: 0,
          forecastSnapshot: fc,
          forecastMeta: { model_id: fc.model_id, source_question: fc.source_question, context_question: fc.context_question, unit: fc.unit, horizon: fc.horizon, top_n: fc.top_n },
          sentinel: { sentinel_id: s.sentinel_id, status: s.status, confidence: s.last_confidence,
            model: s.last_model, reason: s.reason, last_projection: s.last_projection,
            measure_label: s.measure_label || s.label },
        };
      }

      const rep = await api.getReport(reportId);
      const cfg = rep.config || {};
      const visuals = Array.isArray(cfg.visuals) ? [...cfg.visuals] : [];
      visuals.push(newVisual);
      let pageVisuals = cfg.pageVisuals;
      if (pageVisuals && typeof pageVisuals === 'object' && !Array.isArray(pageVisuals)) {
        const firstPage = (cfg.pages && cfg.pages[0]?.id) || Object.keys(pageVisuals)[0] || 'page_1';
        pageVisuals = { ...pageVisuals, [firstPage]: [...(pageVisuals[firstPage] || []), newVisual] };
      }
      let gridLayouts = cfg.gridLayouts;
      const gh = isSummary ? 5 : 6;
      if (gridLayouts && Array.isArray(gridLayouts.lg)) {
        const lg = gridLayouts.lg;
        const nextY = lg.reduce((m, l) => Math.max(m, (l.y || 0) + (l.h || 4)), 0);
        gridLayouts = { ...gridLayouts, lg: [...lg, { i: visualId, x: 0, y: nextY, w: isSummary ? 5 : 12, h: gh, minW: 3, minH: 3 }] };
      } else {
        gridLayouts = { ...(gridLayouts || {}), lg: [{ i: visualId, x: 0, y: 0, w: isSummary ? 5 : 12, h: gh, minW: 3, minH: 3 }] };
      }
      await api.updateReport(reportId, {
        name: rep.name, description: rep.description || '',
        visualization_type: rep.visualization_type || 'line',
        query_definition: rep.query_definition || { model_id: s?.model_id },
        parameters: rep.parameters || [],
        config: { ...cfg, visuals, ...(pageVisuals ? { pageVisuals } : {}), ...(gridLayouts ? { gridLayouts } : {}) },
      });
      setPinPicker({ open: false });
      const publish = async () => {
        const tId = toast.loading('Publishing to dashboard…');
        try {
          await api.promoteReport(reportId, 'test');
          await api.promoteReport(reportId, 'production');
          toast.success('Published — now live on the dashboard', { id: tId, action: { label: 'View', onClick: () => navigate(`/dashboards/view?id=${reportId}`) } });
        } catch (e) { toast.error(e.message || 'Could not publish to dashboard', { id: tId }); }
      };
      toast.success(isSummary ? 'Summary added to dashboard' : 'Sentinel forecast added to dashboard', {
        action: { label: 'Publish to dashboard', onClick: publish },
        cancel: { label: 'Open builder', onClick: () => navigate(`/dashboards/builder?id=${reportId}`) },
        duration: 10000,
      });
    } catch (e) { setPinPicker(p => ({ ...p, saving: false })); toast.error(e.message || 'Failed to pin'); }
  };

  const createAlertFrom = (s) => {
    const p = s.last_projection || {};
    navigate('/alerts', { state: { prefillAlert: {
      name: `Forecast: ${s.measure_label || s.label}`,
      metric: s.measure, model_id: s.model_id,
      operator: p.direction === 'down' ? 'lt' : 'gt',
      threshold: p.value != null ? Math.round(p.value) : '',
      severity: 'medium', predictive: true, forecast_horizon: data?.settings?.horizon || 6, forecast_unit: 'month',
    } } });
  };

  const [investigating, setInvestigating] = useState('');
  const investigate = async (s) => {
    const p = s.last_projection || {};
    const label = s.label || s.measure_label || s.measure;
    const val = p.value != null ? Number(p.value).toLocaleString() : 'the projected level';
    const dir = p.direction === 'down' ? 'decline' : p.direction === 'up' ? 'increase' : 'change';
    const unit = data?.settings?.grain && data.settings.grain !== 'auto' ? data.settings.grain : 'period';
    const question = [
      `Investigate why ${label} is forecast to ${dir} to ${val} by ${p.period || 'the projected period'}.`,
      s.reason ? `The Sentinel flagged this as a projected issue: ${s.reason}.` : '',
      `The forecast used the ${s.last_model || 'auto-selected'} model at ${s.last_confidence || 'unknown'} confidence` +
        `${s.date_field_used ? `, plotted by ${s.date_field_label || s.date_field_used}` : ''}` +
        `${s.last_mape != null ? ` (backtest error ~${Math.round(s.last_mape)}% MAPE)` : ''}.`,
      `Identify the most likely drivers and root causes behind this projected ${dir}, using the underlying data.`,
    ].filter(Boolean).join(' ');
    setInvestigating(s.sentinel_id);
    try {
      const inv = await api.startInvestigation({
        question,
        target: {
          metric: s.measure, model_id: s.model_id, measure_label: label,
          source: 'forecast_sentinel', sentinel_id: s.sentinel_id,
          projected_period: p.period, projected_value: p.value, direction: p.direction,
          confidence: s.last_confidence, model: s.last_model, mape: s.last_mape,
          series: s.series_used, date_field: s.date_field_used, rule: p.rule,
        },
        source: 'forecast_sentinel',
      });
      toast.success('Investigation started — analysing the projected issue…');
      navigate(`/investigations?open=${inv.investigation_id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to start investigation');
    } finally { setInvestigating(''); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading Sentinels…</div>;

  const settings = data?.settings || {};
  const watched = data?.sentinels || [];
  const notWatchable = data?.not_watchable || [];
  const optedOut = data?.opted_out || [];
  const issues = watched.filter(s => s.status === 'projected_issue');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="forecast-sentinels-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-foreground flex items-center gap-2"><Radar className="w-6 h-6 text-indigo-400" /> {fsTerm}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Every certified metric is continuously forecast. You'll be warned before a projected problem happens — with how much to trust it and a one-click alert.</p>
        </div>
        <div className="flex items-center gap-2">
          {refreshing && <span className="flex items-center gap-1 text-[11px] text-indigo-300" data-testid="fs-refreshing"><Loader2 className="w-3 h-3 animate-spin" />Refreshing forecasts…</span>}
          <Button variant="outline" onClick={() => setDetailsOpen(true)} className="rounded-xl" data-testid="fs-details-btn"><HelpCircle className="w-4 h-4 mr-2" />How it works</Button>
          <Button variant="outline" onClick={() => run('scan')} disabled={!!busy} className="rounded-xl" data-testid="fs-scan-btn">{busy === 'scan' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Scan now</Button>
          <Button variant="outline" onClick={() => run('tune')} disabled={!!busy} className="rounded-xl" data-testid="fs-tune-btn">{busy === 'tune' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sliders className="w-4 h-4 mr-2" />}Tune methods</Button>
          <Button variant="outline" onClick={demoTrigger} disabled={!!busy} className="rounded-xl" data-testid="fs-demo-btn">{busy === 'demo' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}Demo issue</Button>
          <Button variant="outline" onClick={openPinSummary} disabled={!!busy} className="rounded-xl" data-testid="fs-pin-summary-btn"><LayoutDashboard className="w-4 h-4 mr-2" />Pin summary</Button>
          <Button onClick={() => { setForm(settings); setSettingsOpen(true); }} className="rounded-xl bg-primary text-primary-foreground" data-testid="fs-settings-btn"><Sliders className="w-4 h-4 mr-2" />Settings</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Watched', watched.length], ['Projected issues', issues.length], ['Not watchable', notWatchable.length], ['Opted out', optedOut.length]].map(([k, v]) => (
          <div key={k} className="glass-card rounded-xl p-3 border border-border/40"><p className="text-[11px] text-muted-foreground">{k}</p><p className="text-2xl font-heading text-foreground">{v}</p></div>
        ))}
      </div>

      {!settings.enabled && <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">Sentinels are turned off. Enable them in Settings to start watching certified metrics.</div>}

      {/* Watched list */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Watched metrics</h2>
        {watched.length === 0 && <p className="text-sm text-muted-foreground">No certified metrics are being watched yet.</p>}
        {watched.map(s => {
          const sm = STATUS_META[s.status] || STATUS_META.pending;
          const Icon = sm.icon;
          return (
            <div key={s.sentinel_id} className="glass-card rounded-xl p-3 border border-border/40 flex items-center justify-between gap-3" data-testid={`fs-row-${s.sentinel_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{s.label || s.measure_label}</span>
                  <Badge variant="outline" className={`text-[9px] ${sm.cls}`}><Icon className="w-2.5 h-2.5 mr-0.5" />{sm.label}</Badge>
                  {s.last_confidence && <Badge className={`text-[9px] cursor-help ${CONF_CLS[s.last_confidence] || CONF_CLS.Unknown}`} title={CONF_HELP[s.last_confidence] || CONF_HELP.Unknown} data-testid={`fs-conf-${s.sentinel_id}`}>{s.last_confidence} confidence</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{s.model_name || s.model_id}{s.last_model ? ` · ${s.last_model}` : ''}{s.reason ? ` · ${s.reason}` : ''}</p>
                {seriesLabelFor(s) && <p className="text-[10px] text-muted-foreground/80 mt-0.5">Forecasting: <span className="text-foreground/80">{seriesLabelFor(s)}</span>{s.series_field ? ' (mapped)' : ''}{dateLabelFor(s) ? <> · by <span className="text-foreground/80">{dateLabelFor(s)}</span>{s.date_field ? ' (pinned)' : ''}</> : ''}</p>}
                {s.last_switch && <p className="text-[10px] text-indigo-300 mt-0.5">Switched {s.last_switch.from || '—'} → {s.last_switch.to} ({s.last_switch.reason})</p>}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {s.status === 'projected_issue' && <Button size="sm" onClick={() => createAlertFrom(s)} className="rounded-lg bg-primary text-primary-foreground text-[11px] h-7" data-testid={`fs-create-alert-${s.sentinel_id}`}><Bell className="w-3 h-3 mr-1" />Create alert</Button>}
                  {s.status === 'projected_issue' && <Button size="sm" variant="outline" onClick={() => investigate(s)} disabled={investigating === s.sentinel_id} className="rounded-lg text-[11px] h-7" data-testid={`fs-investigate-${s.sentinel_id}`} title="Open an AI investigation into why this issue is projected">{investigating === s.sentinel_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Microscope className="w-3 h-3 mr-1" />}Investigate</Button>}
                  <Button size="sm" variant="outline" onClick={() => openChart(s)} className="rounded-lg text-[11px] h-7" data-testid={`fs-chart-${s.sentinel_id}`} title="See the forecast chart for this metric"><LineChart className="w-3 h-3 mr-1" />View chart</Button>
                  <DateSelect s={s} />
                  <MapSelect s={s} placeholder="Auto (metric definition)" />
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 justify-end">
                <Button size="sm" variant="outline" onClick={() => openPin(s)} className="rounded-lg text-[11px] h-7" data-testid={`fs-pin-${s.sentinel_id}`} title="Pin this metric's forecast + sentinel status to a dashboard"><LayoutDashboard className="w-3 h-3 mr-1" />Pin</Button>
                <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground">Watch</span><Switch checked={!s.opted_out} onCheckedChange={() => toggleOptOut(s)} data-testid={`fs-toggle-${s.sentinel_id}`} /></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Not watchable */}
      {notWatchable.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Not watchable yet</h2>
          {notWatchable.map(s => (
            <div key={s.sentinel_id} className="rounded-xl p-3 border border-border/30 bg-muted/20 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <span className="text-sm text-foreground">{s.label || s.measure_label}</span>
                <p className="text-[11px] text-muted-foreground">{s.reason}</p>
                {typeof s.periods_detected === 'number' && (
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5" data-testid={`fs-diag-${s.sentinel_id}`}>
                    Detected {s.periods_detected} usable {s.periods_detected === 1 ? 'period' : 'periods'}{s.min_history ? ` · needs ${s.min_history}` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openChart(s)} className="rounded-lg text-[11px] h-7" data-testid={`fs-chart-${s.sentinel_id}`} title="Preview the forecast chart"><LineChart className="w-3 h-3 mr-1" />View chart</Button>
                <DateSelect s={s} />
                <MapSelect s={s} placeholder="Map series…" />
                <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground">Watch</span><Switch checked={!s.opted_out} onCheckedChange={() => toggleOptOut(s)} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Opted out */}
      {optedOut.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">Opted out</h2>
          {optedOut.map(s => (
            <div key={s.sentinel_id} className="rounded-xl p-2.5 border border-border/20 flex items-center justify-between gap-3 opacity-70">
              <span className="text-sm text-muted-foreground">{s.label || s.measure_label}</span>
              <Button size="sm" variant="ghost" className="text-[11px] h-7" onClick={() => toggleOptOut(s)}>Resume watching</Button>
            </div>
          ))}
        </div>
      )}

      {/* Forecast chart preview */}
      <Dialog open={chartDlg.open} onOpenChange={(o) => !o && setChartDlg({ open: false })}>
        <DialogContent className="glass-card border-border/50 sm:max-w-3xl" data-testid="fs-chart-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><LineChart className="w-4 h-4 text-indigo-400" /> {chartDlg.sentinel?.label || 'Forecast'} — forecast</DialogTitle></DialogHeader>
          {chartDlg.loading ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Building forecast…</div>
          ) : chartDlg.forecast ? (
            <div className="space-y-2">
              <ForecastChart forecast={chartDlg.forecast} />
              <p className="text-[11px] text-muted-foreground">
                {chartDlg.forecast.model}{chartDlg.forecast.date_field_label ? ` · by ${chartDlg.forecast.date_field_label}` : ''}
                {chartDlg.forecast.scorecard?.[0]?.mape != null ? ` · backtest error ${chartDlg.forecast.scorecard[0].mape.toFixed(0)}% (MAPE)` : ''}
              </p>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground text-center px-6">Couldn't build a forecast for this metric yet — check its date field and history under "How it works".</div>
          )}
        </DialogContent>
      </Dialog>

      {/* How it works / details */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="glass-card border-border/50 sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="fs-details-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Info className="w-4 h-4 text-indigo-400" /> How {fsTerm} work</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>Every <span className="text-foreground">certified</span> metric is continuously forecast. If a projected problem is likely, you're warned early — with how much to trust it and a one-click alert.</p>

            <div>
              <p className="text-foreground font-medium mb-1">To be watchable, a metric needs:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>A usable <span className="text-foreground">date/time dimension</span> to plot values over time.</li>
                <li>At least <span className="text-foreground">{settings.min_history || 12} periods</span> of history to learn from.</li>
                <li>A single, computable series (raw column or a calculated measure's formula).</li>
              </ul>
              <p className="mt-1">If any check fails, the metric shows under <span className="text-foreground">Not watchable yet</span> with the exact reason and the number of periods detected.</p>
            </div>

            <div>
              <p className="text-foreground font-medium mb-1">Confidence levels</p>
              <ul className="space-y-1">
                <li><span className="text-emerald-300">High</span> — {CONF_HELP.High}</li>
                <li><span className="text-amber-300">Medium</span> — {CONF_HELP.Medium}</li>
                <li><span className="text-rose-300">Low</span> — {CONF_HELP.Low}</li>
              </ul>
            </div>

            <div>
              <p className="text-foreground font-medium mb-1">Mapping a series</p>
              <p>By default each metric forecasts its own certified definition. Use the <span className="text-foreground">series dropdown</span> on any row to point it at a different underlying column or calculated measure — pick <span className="text-foreground">"Auto (metric definition)"</span> to reset. Changing it re-forecasts immediately, so you can fix a wrong choice anytime.</p>
            </div>

            <div>
              <p className="text-foreground font-medium mb-1">What each watched metric is forecasting</p>
              <div className="rounded-lg border border-border/40 divide-y divide-border/30">
                {watched.length === 0 && <p className="p-2 text-xs">No metrics are being watched yet.</p>}
                {watched.map(s => (
                  <div key={s.sentinel_id} className="flex items-center justify-between gap-2 p-2 text-xs">
                    <span className="text-foreground truncate">{s.label || s.measure_label}</span>
                    <span className="text-muted-foreground truncate text-right">
                      {seriesLabelFor(s) || '—'}{s.series_field ? ' (mapped)' : ''}{s.last_confidence ? ` · ${s.last_confidence}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pin to dashboard picker */}
      <Dialog open={pinPicker.open} onOpenChange={(o) => !o && setPinPicker({ open: false })}>
        <DialogContent className="glass-card border-border/50 sm:max-w-md" data-testid="fs-pin-dialog">
          <DialogHeader><DialogTitle className="font-heading">{pinPicker.summary ? 'Pin projected-issues summary to a dashboard' : 'Pin sentinel forecast to a dashboard'}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">{pinPicker.summary ? "Adds a live tile showing this week's projected issues. Pin it to your home dashboard." : "Adds a live forecast tile with this metric's sentinel status. Only dashboards on the same model are shown."}</p>
          <div className="space-y-1.5 mt-2 max-h-[50vh] overflow-y-auto">
            {(pinPicker.dashboards || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No dashboards on this model yet. Build one from Explore or the dashboard builder first.</p>
            ) : (pinPicker.dashboards || []).map(rep => (
              <button
                key={rep.report_id}
                disabled={!!pinPicker.saving}
                onClick={() => confirmPin(rep.report_id)}
                data-testid={`fs-pin-dash-${rep.report_id}`}
                className="w-full text-left rounded-lg border border-border/40 bg-background/40 hover:bg-muted/40 px-3 py-2 flex items-center justify-between gap-2 disabled:opacity-50"
              >
                <span className="text-sm text-foreground truncate">{rep.name || 'Untitled dashboard'}</span>
                {pinPicker.saving === rep.report_id ? <Loader2 className="w-4 h-4 animate-spin text-indigo-400 shrink-0" /> : <LayoutDashboard className="w-4 h-4 text-muted-foreground shrink-0" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="glass-card border-border/50 sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle className="font-heading">{getTerm('forecast_sentinels', 'singular')} settings</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 mt-2 min-w-0">
              <div className="flex items-center justify-between"><Label className="text-sm">{fsTerm} enabled</Label><Switch checked={!!form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} data-testid="fs-set-enabled" /></div>
              <div className="grid grid-cols-2 gap-3 [&>div]:min-w-0">
                <div><Label className="text-xs text-muted-foreground">Sharp swing (%)</Label><Input type="number" value={form.swing_pct} onChange={e => setForm(f => ({ ...f, swing_pct: Number(e.target.value) }))} className="mt-1 bg-muted/50 border-border/50" /></div>
                <div><Label className="text-xs text-muted-foreground">Cumulative move (%)</Label><Input type="number" value={form.cumulative_pct} onChange={e => setForm(f => ({ ...f, cumulative_pct: Number(e.target.value) }))} className="mt-1 bg-muted/50 border-border/50" /></div>
                <div><Label className="text-xs text-muted-foreground">Min history (periods)</Label><Input type="number" value={form.min_history} onChange={e => setForm(f => ({ ...f, min_history: Number(e.target.value) }))} className="mt-1 bg-muted/50 border-border/50" /></div>
                <div><Label className="text-xs text-muted-foreground">Forecast horizon</Label><Input type="number" value={form.horizon} onChange={e => setForm(f => ({ ...f, horizon: Number(e.target.value) }))} className="mt-1 bg-muted/50 border-border/50" /></div>
              </div>
              <div className="flex items-center justify-between"><Label className="text-sm">Use governance targets/bands</Label><Switch checked={!!form.use_targets} onCheckedChange={v => setForm(f => ({ ...f, use_targets: v }))} /></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Auto-apply weekly method switch</Label><Switch checked={!!form.tuning_auto_apply} onCheckedChange={v => setForm(f => ({ ...f, tuning_auto_apply: v }))} /></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Auto-investigate new projected issues</Label><Switch checked={!!form.auto_investigate} onCheckedChange={v => setForm(f => ({ ...f, auto_investigate: v }))} data-testid="fs-set-auto-investigate" /></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Push notifications (email/Slack/Teams)</Label><Switch checked={!!form.notify} onCheckedChange={v => setForm(f => ({ ...f, notify: v, notify_channels: v ? (f => f)(form.notify_channels?.length ? form.notify_channels : ['email']) : form.notify_channels }))} data-testid="fs-set-notify" /></div>
              <p className="text-[10px] text-muted-foreground">Warnings always appear in the Insights feed and here. Push is off by default to keep projections low-noise.</p>
              <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={() => setSettingsOpen(false)} className="rounded-lg">Cancel</Button><Button onClick={saveSettings} className="rounded-lg bg-primary text-primary-foreground" data-testid="fs-set-save">Save</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
